import { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
  IconButton,
  Collapse,
} from "@mui/material";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useNavigate, useLocation } from "react-router-dom";
import companyLogo from "../../assets/Ytc_light.png";

const drawerWidth = 270;

const Sidebar = ({ mobileOpen, setMobileOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const userName = sessionStorage.getItem("name") || "Admin User";
  const userEmail = sessionStorage.getItem("email");
  const isLogsAllowed = userEmail.toLowerCase() === "info@ytctechventures.com";

  const menuItems = [
    {
      label: "Dashboard",
      icon: <DashboardRoundedIcon />,
      path: "/dashboard",
    },
    {
      label: "Report",
      icon: <AssessmentRoundedIcon />,
      path: "/report",
    },
    {
      label: "IVR-Dashboard",
      icon: <PhoneInTalkRoundedIcon />,
      path: "/ivr-dashboard",
      matchPaths: ["/ivr-dashboard", "/ivr-anaylitics"],
    },
    {
      label: "Mandi Partner",
      icon: <StorefrontRoundedIcon />,
      path: "/MandiPartner",
    },

    {
      label: "Buyer",
      icon: <PeopleAltRoundedIcon />,
      path: "/buyers",
    },
  ];

  const managementChildren = [
    {
      label: "Variety",
      icon: <CategoryRoundedIcon />,
      path: "/variety",
    },
    {
      label: "Admin",
      icon: <PersonRoundedIcon />,
      path: "/Admins",
    },
    ...(isLogsAllowed
      ? [
          {
            label: "Logs",
            icon: <ArticleRoundedIcon />,
            path: "/logs",
          },
        ]
      : []),
  ];

  const isManagementActive = managementChildren.some(
    (child) => location.pathname === child.path
  );

  const [managementOpen, setManagementOpen] = useState(isManagementActive);

  useEffect(() => {
    if (isManagementActive) setManagementOpen(true);
  }, [isManagementActive]);

  const handleNavigate = (path) => {
    navigate(path);

    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        background:
          "linear-gradient(180deg, #0F172A 0%, #111827 45%, #1E293B 100%)",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 25px rgba(37,99,235,0.35)",
              p: 0.75,
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={companyLogo}
              alt="Company Logo"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>

          <Box>
            <Typography fontWeight={800} fontSize="1rem">
              Call Analysis
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.65)",
              }}
            >
              CRM Dashboard
            </Typography>
          </Box>
        </Stack>

        {isMobile && (
          <IconButton
            onClick={() => setMobileOpen(false)}
            sx={{
              color: "#fff",
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        )}
      </Box>

      <Divider
        sx={{
          borderColor: "rgba(255,255,255,0.08)",
          flexShrink: 0,
        }}
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 2,
          py: 3,
        }}
      >
        <List>
          {menuItems.map((item) => {
            const active = item.matchPaths
              ? item.matchPaths.includes(location.pathname)
              : location.pathname === item.path;

            return (
              <ListItemButton
                key={item.label}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  mb: 1,
                  borderRadius: "14px",
                  py: 1.4,

                  background: active
                    ? "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)"
                    : "transparent",

                  "&:hover": {
                    background: active
                      ? "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)"
                      : "rgba(255,255,255,0.06)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "#fff",
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: active ? 700 : 500,
                    fontSize: "0.95rem",
                  }}
                />
              </ListItemButton>
            );
          })}

          <ListItemButton
            onClick={() => setManagementOpen((prev) => !prev)}
            sx={{
              mb: managementOpen ? 0.5 : 1,
              borderRadius: "14px",
              py: 1.4,

              background: isManagementActive
                ? "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)"
                : "transparent",

              "&:hover": {
                background: isManagementActive
                  ? "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)"
                  : "rgba(255,255,255,0.06)",
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: "#fff",
                minWidth: 40,
              }}
            >
              <SettingsRoundedIcon />
            </ListItemIcon>

            <ListItemText
              primary="Management"
              primaryTypographyProps={{
                fontWeight: isManagementActive ? 700 : 500,
                fontSize: "0.95rem",
              }}
            />

            <ExpandMoreRoundedIcon
              sx={{
                fontSize: 20,
                transition: "transform 0.2s ease",
                transform: managementOpen
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </ListItemButton>

          <Collapse in={managementOpen} timeout="auto" unmountOnExit>
            <List disablePadding sx={{ mb: 1 }}>
              {managementChildren.map((item) => {
                const active = location.pathname === item.path;

                return (
                  <ListItemButton
                    key={item.label}
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      mb: 0.5,
                      ml: 1.5,
                      pl: 2,
                      borderRadius: "12px",
                      py: 1.1,
                      borderLeft: "2px solid rgba(255,255,255,0.12)",

                      background: active
                        ? "rgba(37,99,235,0.25)"
                        : "transparent",

                      "&:hover": {
                        background: active
                          ? "rgba(37,99,235,0.3)"
                          : "rgba(255,255,255,0.06)",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: "#fff",
                        minWidth: 34,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>

                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: active ? 700 : 500,
                        fontSize: "0.88rem",
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Collapse>
        </List>
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5, pt: 1, flexShrink: 0 }}>
        <Box
          sx={{
            background: "rgba(255,255,255,0.06)",
            borderRadius: "14px",
            p: 1.25,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
              sx={{
                bgcolor: "#2563EB",
                fontWeight: 700,
                width: 32,
                height: 32,
                fontSize: "0.85rem",
              }}
            >
              {userName?.charAt(0)?.toUpperCase()}
            </Avatar>

            <Box flex={1} minWidth={0}>
              <Typography fontWeight={700} fontSize="0.82rem" lineHeight={1.3} noWrap>
                {userName}
              </Typography>

              <Typography
                fontSize="0.72rem"
                lineHeight={1.3}
                noWrap
                sx={{
                  color: "rgba(255,255,255,0.65)",
                }}
              >
                {userEmail}
              </Typography>
            </Box>

            <IconButton
              onClick={handleLogout}
              size="small"
              sx={{
                color: "#EF4444",
                bgcolor: "rgba(239,68,68,0.12)",
                "&:hover": {
                  bgcolor: "rgba(239,68,68,0.2)",
                },
              }}
            >
              <LogoutRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          variant="temporary"
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              border: "none",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          open
          sx={{
            width: drawerWidth,
            flexShrink: 0,

            "& .MuiDrawer-paper": {
              width: drawerWidth,
              border: "none",
              boxSizing: "border-box",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;